import { isNumber } from "@common/helpers/typeGuards";
import { useMemo } from "react";
import { Entity } from "@common/types/entity";
import {
    FinancialAccount,
    FinancialAccountSourceType,
    FinancialAccountType,
} from "@common/types/financialAccount";
import { useConstant } from "@hooks/useConstant";
import { useEntitiesContexts } from "./useEntitiesContexts";

export interface UseFinancialAccountsOptions {
    entityId?: number;
    entityIds?: number[];
    excludeNoBalanceAccounts?: boolean;
    onlyBusiness?: boolean;
    excludeMockEntities?: boolean;
    sourceTypes?: FinancialAccountSourceType[];
    connectionId?: number;
    connectionIds?: number[];
    createTempCashAccounts?: boolean;
    isSingleSelect?: boolean;
    excludeNonManualAccounts?: boolean;
    excludedAccountIds?: number[];
}

const PLAID_SOURCE_TYPES = [FinancialAccountSourceType.PLAID];
const GUSTO_SOURCE_TYPES = [FinancialAccountSourceType.GUSTO];

export function usePlaidFinancialAccounts(
    options: UseFinancialAccountsOptions = {},
) {
    return useFinancialAccounts({
        ...options,
        sourceTypes: PLAID_SOURCE_TYPES,
    });
}

export function usePayrollFinancialAccounts(
    options: UseFinancialAccountsOptions = {},
) {
    return useFinancialAccounts({
        ...options,
        sourceTypes: GUSTO_SOURCE_TYPES,
    });
}

export function useFinancialAccounts({
    entityId,
    entityIds: entityIdsOption,
    sourceTypes,
    connectionId,
    connectionIds: connectionIdsOption,
    excludedAccountIds,
    excludeNoBalanceAccounts = false,
    onlyBusiness = false,
    excludeMockEntities = false,
    createTempCashAccounts = false,
    excludeNonManualAccounts = false,
}: UseFinancialAccountsOptions = {}) {
    const connectionIds = useMemo(
        () => (connectionId ? [connectionId] : connectionIdsOption),
        [connectionId, connectionIdsOption],
    );
    const entityIds = useMemo(
        () => (entityId ? [entityId] : entityIdsOption),
        [entityId, entityIdsOption],
    );

    const entitiesContexts = useEntitiesContexts({
        onlyBusiness,
        excludeMockEntities,
    });
    const tempCashAccountsCreatedAt = useConstant(() => new Date());

    return useMemo(() => {
        let accounts = (entitiesContexts ?? [])
            .filter((ec) => !entityIds || entityIds.includes(ec.entity.id))
            .map((e) => e.financialAccounts)
            .flat()
            .filter(
                (fa) =>
                    !excludedAccountIds || !excludedAccountIds.includes(fa.id),
            );

        if (createTempCashAccounts) {
            const cashAccounts: FinancialAccount[] = (entitiesContexts ?? [])
                .filter((ec) => !entityIds || entityIds.includes(ec.entity.id))
                .filter((ec) =>
                    ec.financialAccounts.every(
                        (financialAccount) =>
                            financialAccount.financialType !==
                            FinancialAccountType.CASH,
                    ),
                )
                .map((ec) => ({
                    id: -1 * ec.entity.id,
                    isBusiness: ec.entity.isBusiness,
                    name: "Cash",
                    balance: 0,
                    balanceSyncedAt: tempCashAccountsCreatedAt,
                    transactionsSyncedAt: tempCashAccountsCreatedAt,
                    continuousReconciliationEnabledAt: null,
                    createdAt: tempCashAccountsCreatedAt,
                    entity: ec.entity,
                    entityId: ec.entity.id,
                    sourceType: FinancialAccountSourceType.MANUAL,
                    routingNumber: null,
                    wireRoutingNumber: null,
                    accountNumberMask: null,
                    disabled: false,
                    financialType: FinancialAccountType.CASH,
                    order: ec.financialAccounts.length + 1,
                    institutionName: null,
                    subaccounts: [],
                    bookkeepingStartDate: null,
                    bookkeepingEndDate: null,
                }));

            accounts.push(...cashAccounts);
        }

        if (connectionIds) {
            accounts = accounts.filter((a) =>
                a.integrationAccounts
                    ?.map((ia) => ia.connectionId)
                    .some((cid) => cid && connectionIds.includes(cid)),
            );
        }
        if (excludeNoBalanceAccounts) {
            accounts = accounts.filter((a) => isNumber(a.balance));
        }
        if (sourceTypes?.length) {
            accounts = accounts.filter(
                (a) => a.sourceType && sourceTypes.includes(a.sourceType),
            );
        }
        if (excludeNonManualAccounts) {
            accounts = accounts.filter(
                (account) =>
                    account.sourceType === FinancialAccountSourceType.MANUAL ||
                    account.sourceType ===
                        FinancialAccountSourceType.FILE_IMPORT,
            );
        }

        return accounts;
    }, [
        entitiesContexts,
        entityIds,
        connectionIds,
        excludeNoBalanceAccounts,
        sourceTypes,
        createTempCashAccounts,
        excludeNonManualAccounts,
        excludedAccountIds,
        tempCashAccountsCreatedAt,
    ]);
}

export function getTotalBalance(allAccounts: FinancialAccount[]) {
    return allAccounts
        .filter((acc) => !acc.disabled && isNumber(acc.balance))
        .reduce(($, acc) => $ + (acc.balance ?? 0), 0);
}

export function useTotalBalance(entity?: Entity) {
    const allAccounts = useFinancialAccounts({
        entityId: entity?.id,
    });

    return getTotalBalance(allAccounts);
}
